/**
 * The dashboard layout as the operator's own — one arrangement for every board they open.
 *
 * The All Sites dashboard and each site's dashboard are the same card board, so they share one
 * layout: a card moved, resized or put on "Last 7 days" in one scope is the same in the other,
 * and a card only one scope offers (a model just one site runs) keeps its place while it is
 * absent. It is kept on the ACCOUNT (AuthContext → /auth/me/dashboard-layout), so it follows the
 * operator to another browser; the browser copy under `layoutKey` is the cache and the fallback
 * for a backend that cannot store one. The key carries the user id, so two people sharing a
 * workstation never inherit each other's board.
 */
import { useMemo } from 'react';
import type { BoardLayoutItem, BoardLayoutRemote } from '@trinetra/shared';
import { useAuth } from '../contexts/AuthContext';

/** Versioned: a change to the card catalogue's meaning bumps it rather than misreading old layouts. */
const LAYOUT_KEY = 'trinetra.cloud.dashboard.layout.v2';

export function useAccountDashboardLayout(): { layoutKey: string; layoutRemote: BoardLayoutRemote } {
  const { user, saveDashboardLayout } = useAuth();
  const saved = user?.dashboard_layout;
  const layoutRemote = useMemo<BoardLayoutRemote>(
    () => ({
      initial: saved,
      save: (items: BoardLayoutItem[] | null) => saveDashboardLayout(items),
    }),
    // saveDashboardLayout is recreated with the provider's render; the board reads `save`
    // through a ref, so only the saved value decides when this changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [saved],
  );
  return { layoutKey: `${LAYOUT_KEY}.${user?.id || 'anon'}`, layoutRemote };
}
